import { useRightPanel } from '@/contexts/RightPanelContext';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { 
  PanelLeftClose, 
  PanelLeftOpen,
  Pin,
  PinOff,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { InsightsPanel } from './InsightsPanel';

export interface RightPanelTab {
  id: string;
  label: string;
  icon: LucideIcon;
  content: React.ReactNode;
  showBadge?: boolean;
  badgeCount?: number;
}

interface RightPanelBaseProps {
  readonly tabs: RightPanelTab[];
  readonly defaultTab?: string;
  readonly 'data-tour'?: string;
}

/**
 * Base RightPanel component that accepts custom tab configurations.
 * Each page can define its own tabs with page-specific content.
 * 
 * Usage:
 * <RightPanelBase 
 *   tabs={[
 *     { id: 'actions', label: 'Actions', icon: Zap, content: <ActionsTab /> },
 *     { id: 'filters', label: 'Filters', icon: Filter, content: <FiltersTab /> }
 *   ]}
 * />
 */
export function RightPanelBase({ tabs, defaultTab, 'data-tour': dataTour }: Readonly<RightPanelBaseProps>) {
  const {
    isExpanded,
    setIsExpanded,
    activeTab,
    setActiveTab,
    isPinned,
    setIsPinned,
    panelWidth,
    insightsPanelWidth,
    showInsights,
    selectedRowIds,
    activeFilterCount,
    clearAllFilters,
  } = useRightPanel();

  const getExpandedWidth = () => {
    if (showInsights) {
      return insightsPanelWidth + panelWidth;
    }
    return panelWidth;
  };
  const totalWidth = isExpanded ? getExpandedWidth() : 56;

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleTabChange = (value: string) => {
    // If clicking the already active tab, toggle the panel
    if (activeTab === value && isExpanded) {
      setIsExpanded(false);
    } else {
      setActiveTab(value as any);
      if (!isExpanded) {
        setIsExpanded(true);
      }
    }
  };

  const currentTab = tabs.find(t => t.id === activeTab) || tabs[0];

  return (
    <div
      className={cn(
        "absolute right-0 top-0 bottom-0 bg-background flex transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] z-40",
        isExpanded ? "border-l-4" : "border-l"
      )}
      style={{ width: `${totalWidth}px` }}
      data-tour={dataTour}
    >
      {/* Insights Panel (Optional, leftmost when visible) */}
      {isExpanded && showInsights && (
        <InsightsPanel width={insightsPanelWidth} />
      )}

      {/* Main Content Area (middle, conditional) */}
      {isExpanded && (
        <div 
          className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-background"
          style={{ width: `${panelWidth - 56}px` }}
        >
          {/* Header */}
          <div className="h-14 border-b flex items-center justify-between px-4 flex-shrink-0">
            <div className="flex items-center gap-2">
              <h2 className="font-semibold text-sm">
                {currentTab?.label ?? 'Panel'}
              </h2>
              {/* Show badge for Actions tab when items are selected */}
              {currentTab?.id === 'actions' && selectedRowIds.size > 0 && (
                <span className="inline-flex items-center justify-center h-6 px-2.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold">
                  {selectedRowIds.size} selected
                </span>
              )}
              {/* Show badge for other tabs if configured */}
              {currentTab?.id !== 'actions' && currentTab?.badgeCount !== undefined && currentTab.badgeCount > 0 && (
                <span className="inline-flex items-center justify-center h-5 px-2 rounded-full bg-primary/10 text-primary text-xs font-medium">
                  {currentTab.badgeCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {currentTab?.id === 'filters' && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={clearAllFilters}
                >
                  Clear All
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setIsPinned(!isPinned)}
                title={isPinned ? 'Unpin panel' : 'Pin panel'}
              >
                {isPinned ? (
                  <PinOff className="h-4 w-4" />
                ) : (
                  <Pin className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <Tabs value={activeTab} onValueChange={handleTabChange} className="h-full">
              {tabs.map(tab => (
                <TabsContent key={tab.id} value={tab.id} className="m-0 h-full">
                  {tab.content}
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </div>
      )}

      {/* Icon Rail (rightmost, always visible) */}
      <div className="w-14 border-l bg-white dark:bg-black flex flex-col items-center py-4 gap-2 flex-shrink-0" data-testid="right-panel-tabs">
        {/* Toggle Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleToggle}
          className="h-10 w-10"
          title={isExpanded ? 'Collapse panel' : 'Expand panel'}
        >
          {isExpanded ? (
            <PanelLeftClose className="h-5 w-5" />
          ) : (
            <PanelLeftOpen className="h-5 w-5" />
          )}
        </Button>

        <Separator className="w-8" />

        {/* Tab Icons */}
        {tabs.map(tab => {
          const Icon = tab.icon;
          const badgeCount = tab.showBadge ? (tab.badgeCount ?? selectedRowIds.size) : 0;
          
          return (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'ghost'}
              onClick={() => handleTabChange(tab.id)}
              className={cn(
                "h-auto w-10 relative flex flex-col items-center gap-1 py-2 px-1",
                activeTab === tab.id && 'bg-primary text-primary-foreground'
              )}
              title={tab.label}
              data-testid={`right-panel-tab-${tab.id}`}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="text-[9px] leading-tight text-center font-medium">
                {tab.label}
              </span>
              {badgeCount > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-medium">
                  {badgeCount}
                </span>
              )}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
